import { Box } from "@mantine/core";
import { useEffect, useRef, useState } from "react";

/**
 * MeshGradientBackground - Diaspora Modern animated gradient background
 * Features:
 * - 5 gradient layers mapped to the four pillar accents + terracotta signature
 * - Warm espresso base (theme.other.backgroundColor)
 * - Mouse parallax interaction
 * - Moderate intensity animation
 */
export const MeshGradientBackground = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Diaspora Modern palette
  // Terracotta signature + pillar accents (slate teal, ochre, coral flare, sage)
  const gradientLayers = [
    {
      // Terracotta — signature atmospheric glow
      gradients: [
        "radial-gradient(circle at 20% 30%, rgba(212,85,43,0.32) 0%, rgba(212,85,43,0.14) 40%, rgba(212,85,43,0.04) 70%, transparent 100%)",
        "radial-gradient(circle at 80% 70%, rgba(184,68,33,0.24) 0%, rgba(184,68,33,0.10) 40%, rgba(184,68,33,0.03) 70%, transparent 100%)",
        "radial-gradient(circle at 50% 50%, rgba(212,85,43,0.18) 0%, rgba(212,85,43,0.06) 50%, transparent 100%)",
      ],
      animation: "meshMove1 20s ease-in-out infinite",
      parallax: { x: 0.02, y: 0.02 },
      blur: "60px",
    },
    {
      // Ochre — Dance pillar warmth
      gradients: [
        "radial-gradient(circle at 80% 20%, rgba(184,132,61,0.28) 0%, rgba(184,132,61,0.12) 40%, rgba(184,132,61,0.04) 70%, transparent 100%)",
        "radial-gradient(circle at 20% 80%, rgba(153,109,49,0.20) 0%, rgba(153,109,49,0.10) 40%, rgba(153,109,49,0.03) 70%, transparent 100%)",
        "radial-gradient(circle at 50% 50%, rgba(184,132,61,0.14) 0%, rgba(184,132,61,0.06) 50%, transparent 100%)",
      ],
      animation: "meshMove2 25s ease-in-out infinite",
      parallax: { x: -0.015, y: 0.015 },
      blur: "70px",
    },
    {
      // Slate teal — Tech pillar quiet depth
      gradients: [
        "radial-gradient(circle at 50% 50%, rgba(123,164,165,0.16) 0%, rgba(123,164,165,0.07) 50%, transparent 100%)",
        "radial-gradient(circle at 10% 90%, rgba(78,118,118,0.18) 0%, rgba(78,118,118,0.07) 40%, transparent 100%)",
        "radial-gradient(circle at 90% 10%, rgba(78,118,118,0.16) 0%, rgba(78,118,118,0.06) 50%, transparent 100%)",
      ],
      animation: "meshMove3 30s ease-in-out infinite",
      parallax: { x: 0.01, y: -0.01 },
      blur: "80px",
    },
    {
      // Sage — Think pillar reflection
      gradients: [
        "radial-gradient(circle at 60% 80%, rgba(74,107,78,0.32) 0%, rgba(74,107,78,0.14) 40%, rgba(74,107,78,0.04) 70%, transparent 100%)",
        "radial-gradient(circle at 40% 20%, rgba(58,86,60,0.22) 0%, rgba(58,86,60,0.10) 40%, rgba(58,86,60,0.03) 70%, transparent 100%)",
        "radial-gradient(circle at 50% 50%, rgba(74,107,78,0.14) 0%, rgba(74,107,78,0.06) 50%, transparent 100%)",
      ],
      animation: "meshMove4 22s ease-in-out infinite",
      parallax: { x: -0.02, y: 0.02 },
      blur: "65px",
    },
    {
      // Coral flare — Media pillar energy
      gradients: [
        "radial-gradient(circle at 30% 20%, rgba(232,93,60,0.22) 0%, rgba(232,93,60,0.10) 40%, rgba(232,93,60,0.03) 70%, transparent 100%)",
        "radial-gradient(circle at 70% 80%, rgba(212,85,43,0.16) 0%, rgba(212,85,43,0.07) 40%, rgba(212,85,43,0.03) 70%, transparent 100%)",
        "radial-gradient(circle at 50% 50%, rgba(232,93,60,0.14) 0%, rgba(232,93,60,0.05) 50%, transparent 100%)",
      ],
      animation: "meshMove5 28s ease-in-out infinite",
      parallax: { x: 0.015, y: -0.015 },
      blur: "75px",
    },
  ];

  return (
    <>
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes meshMove1 {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(30px, -20px) scale(1.1); }
            66% { transform: translate(-20px, 30px) scale(0.9); }
          }
          @keyframes meshMove2 {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(-25px, 25px) scale(1.15); }
            66% { transform: translate(25px, -25px) scale(0.85); }
          }
          @keyframes meshMove3 {
            0%, 100% { transform: translate(0, 0) scale(1); }
            50% { transform: translate(20px, 20px) scale(1.2); }
          }
          @keyframes meshMove4 {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(-30px, -15px) scale(1.1); }
            66% { transform: translate(15px, 30px) scale(0.9); }
          }
          @keyframes meshMove5 {
            0%, 100% { transform: translate(0, 0) scale(1); }
            50% { transform: translate(-20px, -30px) scale(1.15); }
          }
        }
      `}</style>
      <Box
        ref={containerRef}
        style={{
          position: "fixed",
          top: "-10%",
          left: "-10%",
          width: "120vw",
          height: "120vh",
          zIndex: 1.5,
          overflow: "hidden",
          pointerEvents: "none",
          opacity: 0.3,
        }}
      >
        {gradientLayers.map((layer, index) => {
          const parallaxX = mousePosition.x * layer.parallax.x;
          const parallaxY = mousePosition.y * layer.parallax.y;
          
          return (
            <Box
              key={index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: layer.gradients.join(", "),
                animation: layer.animation,
                transform: `translate(${parallaxX}px, ${parallaxY}px)`,
                transition: "transform 0.1s ease-out",
                mixBlendMode: "screen",
                filter: `blur(${layer.blur})`,
                WebkitFilter: `blur(${layer.blur})`,
              }}
            />
          );
        })}
      </Box>
    </>
  );
};
